import React, { useEffect, useMemo, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { UserRole } from "../../types/auth";
import { NAV_GROUPS } from "../../config/navigation";
import { LogOut, Menu, X, CalendarDays } from "lucide-react";

const ROLE_LABEL: Record<UserRole, string> = {
  superadmin: "Super Admin",
  admin: "Admin",
  roommaster: "Room Master",
  kiemkho: "Kiểm Kho",
  user: "User",
};

const ROLE_BADGE: Record<UserRole, string> = {
  superadmin: "bg-amber-500/15 text-amber-300 ring-amber-400/20",
  admin: "bg-coffee-500/15 text-coffee-300 ring-coffee-400/20",
  roommaster: "bg-sky-500/15 text-sky-300 ring-sky-400/20",
  kiemkho: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/20",
  user: "bg-zinc-500/15 text-zinc-300 ring-zinc-400/20",
};

function Layout(): React.ReactElement {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  const userRole = user?.role;

  // Chỉ giữ những mục (và nhóm) mà vai trò hiện tại được phép vào
  const groups = useMemo(
    () =>
      NAV_GROUPS.map((g) => ({
        ...g,
        items: g.items.filter((i) => (userRole ? i.roles.includes(userRole) : false)),
      })).filter((g) => g.items.length > 0),
    [userRole]
  );

  const isActive = (path: string): boolean => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const current = useMemo(() => {
    for (const g of groups) {
      const item = g.items.find((i) => isActive(i.path));
      if (item) return { group: g.title, label: item.label };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, location.pathname]);

  // Đóng thanh bên trên điện thoại khi chuyển trang
  useEffect(() => { setIsSidebarOpen(false); }, [location.pathname]);

  const today = new Date().toLocaleDateString("vi-VN", { weekday: "long", day: "numeric", month: "numeric" });
  const initial = user?.name?.charAt(0).toUpperCase() ?? "?";

  return (
    <div className="flex h-screen bg-coffee-50">
      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-zinc-900 text-zinc-100
          flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        {/* Logo */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 h-16 border-b border-white/5">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <img src="/logo.png" alt="23Homestay" className="h-9 w-auto brightness-0 invert" />
          </Link>
          <button
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Đóng menu"
            className="lg:hidden text-zinc-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/5"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav */}
        <div className="relative flex-1 min-h-0">
          <nav className="h-full overflow-y-auto px-3 py-4 space-y-5 [scrollbar-color:rgba(255,255,255,0.14)_transparent]">
            {groups.map((group) => (
              <div key={group.title}>
                {groups.length > 1 && (
                  <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                    {group.title}
                  </p>
                )}
                <ul className="space-y-0.5">
                  {group.items.map((item) => {
                    const active = isActive(item.path);
                    const Icon = item.icon;
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          aria-current={active ? "page" : undefined}
                          className={`
                            group relative flex items-center gap-3 pl-3 pr-3 py-2 rounded-lg text-sm
                            transition-colors duration-150
                            ${active
                              ? "bg-coffee-600/20 text-white font-medium"
                              : "text-zinc-400 hover:text-zinc-100 hover:bg-white/5"}
                          `}
                        >
                          {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-coffee-500" />}
                          <Icon size={18} className={`flex-shrink-0 ${active ? "text-coffee-400" : "text-zinc-500 group-hover:text-zinc-300"}`} />
                          <span className="truncate">{item.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
          {/* mờ dần ở đáy để biết danh sách còn cuộn được */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-zinc-900 to-transparent" />
        </div>

        {/* User footer */}
        <div className="flex-shrink-0 p-3 border-t border-white/5">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-coffee-500 to-coffee-700 flex items-center justify-center flex-shrink-0 ring-2 ring-white/10">
              <span className="text-white font-semibold text-sm">{initial}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-zinc-100 truncate">{user?.name}</p>
              {userRole && (
                <span className={`inline-block mt-0.5 px-2 py-px rounded-full text-[10px] font-medium ring-1 ${ROLE_BADGE[userRole]}`}>
                  {ROLE_LABEL[userRole]}
                </span>
              )}
            </div>
            <button
              onClick={logout}
              title="Đăng xuất"
              aria-label="Đăng xuất"
              className="p-2 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-white/5 transition-colors"
            >
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-[1px] z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="flex-shrink-0 h-14 bg-white/90 backdrop-blur border-b border-zinc-200/80 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setIsSidebarOpen(true)}
              aria-label="Mở menu"
              className="lg:hidden -ml-1 p-2 rounded-lg text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 transition-colors"
            >
              <Menu size={20} />
            </button>
            {current && (
              <div className="min-w-0 flex items-center gap-2 text-sm">
                <span className="hidden sm:inline text-zinc-400">{current.group}</span>
                <span className="hidden sm:inline text-zinc-300">/</span>
                <span className="font-semibold text-zinc-800 truncate">{current.label}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-zinc-500">
            <span className="hidden md:flex items-center gap-1.5 capitalize">
              <CalendarDays size={15} className="text-zinc-400" />
              {today}
            </span>
            <span className="hidden md:block h-5 w-px bg-zinc-200" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-coffee-500 to-coffee-700 flex items-center justify-center">
                <span className="text-white font-semibold text-xs">{initial}</span>
              </div>
              <span className="hidden sm:block font-medium text-zinc-700">{user?.name}</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto bg-coffee-50">
          <div className="p-4 sm:p-5 lg:p-6 max-w-[1400px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export default Layout;
